import { useState } from "react";
import { TeamLogo } from "../shared/TeamLogo";

const FILTERS = ["All", "Full Game", "First 5", "NRFI", "YRFI", "Wins", "Losses", "Pending"];

const RESULT_STYLE = {
  WIN: "text-emerald-400",
  LOSS: "text-red-400",
  PUSH: "text-yellow-400",
  PENDING: "text-zinc-500",
};

const formatDate = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
};

const applyFilter = (picks, filter) => {
  switch (filter) {
    case "Full Game":
      return picks.filter((p) => p.market_type === "FULL_GAME");
    case "First 5":
      return picks.filter((p) => p.market_type === "F5");
    case "NRFI":
      return picks.filter((p) => p.market_type === "NRFI");
    case "YRFI":
      return picks.filter((p) => p.market_type === "YRFI");
    case "Wins":
      return picks.filter((p) => p.result === "WIN");
    case "Losses":
      return picks.filter((p) => p.result === "LOSS");
    case "Pending":
      return picks.filter((p) => p.result === "PENDING");
    default:
      return picks;
  }
};

const ORIGIN_LABEL = { forward_verified: "Forward", historical_backfill: "Historical", legacy_unverified: "Legacy" };

const formatPick = (p) => {
  if (p.is_first_inning) return p.market_price_available ? p.side : `${p.side} (no market price)`;
  return `${p.side === "OVER" ? "Over" : "Under"} ${p.market_line}`;
};

export const BestBetsTable = ({ picks }) => {
  const [filter, setFilter] = useState("All");
  const filtered = applyFilter(picks, filter);

  return (
    <div data-testid="best-bets-table-section" className="flex flex-col gap-3">
      <div data-testid="best-bets-filters" className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            data-testid={`best-bets-filter-${f.toLowerCase().replace(" ", "-")}`}
            onClick={() => setFilter(f)}
            className={`rounded-full border px-3 py-1.5 font-mono text-xs uppercase tracking-wide transition-colors ${
              filter === f ? "border-eb-accent bg-eb-accent/10 text-eb-accent" : "border-white/10 text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p data-testid="best-bets-table-empty" className="rounded-lg border border-dashed border-zinc-800 bg-eb-surface/40 px-6 py-10 text-center font-mono text-xs text-zinc-600">
          No picks match this filter yet.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-white/10 bg-eb-surface">
          <table data-testid="best-bets-table" className="w-full font-mono text-xs">
            <thead>
              <tr className="border-b border-white/10 text-left text-zinc-500">
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Matchup</th>
                <th className="px-3 py-2">Market</th>
                <th className="px-3 py-2">Pick</th>
                <th className="px-3 py-2">Model Confidence</th>
                <th className="px-3 py-2">Projected Total</th>
                <th className="px-3 py-2">Actual</th>
                <th className="px-3 py-2">Result</th>
                <th className="px-3 py-2">Origin</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`best-bets-row-${p.id}`} className="border-b border-white/5 text-zinc-300 last:border-0">
                  <td className="px-3 py-2 text-zinc-500">{formatDate(p.game_date)}</td>
                  <td className="px-3 py-2">
                    <span className="flex items-center gap-1.5">
                      <TeamLogo
                        team={{ name: p.away_team_name, abbreviation: p.away_team_abbreviation, logo_url: p.away_team_logo_url }}
                        size={18}
                        testId={`best-bets-row-${p.id}-logo-away`}
                      />
                      {p.away_team_name} @
                      <TeamLogo
                        team={{ name: p.home_team_name, abbreviation: p.home_team_abbreviation, logo_url: p.home_team_logo_url }}
                        size={18}
                        testId={`best-bets-row-${p.id}-logo-home`}
                      />
                      {p.home_team_name}
                    </span>
                  </td>
                  <td className="px-3 py-2">{p.market_label}</td>
                  <td className="px-3 py-2">{formatPick(p)}</td>
                  <td className="px-3 py-2">{(p.probability * 100).toFixed(0)}%</td>
                  <td className="px-3 py-2">{p.projected_total ?? "--"}</td>
                  <td className="px-3 py-2">{p.actual_runs ?? "--"}</td>
                  <td className={`px-3 py-2 font-heading ${RESULT_STYLE[p.result]}`} data-testid={`best-bets-row-${p.id}-result`}>
                    {p.result}
                  </td>
                  <td className="px-3 py-2 text-zinc-500" data-testid={`best-bets-row-${p.id}-origin`}>
                    {ORIGIN_LABEL[p.provenance] || "Legacy"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
